import React from "react"
import {twMerge} from "tailwind-merge"

import {GenericErrorAlert} from "../../../components/Alert.tsx"
import {Loading} from "../../../components/Loading.tsx"

export type TCardStateQuery = {
  isPending: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => Promise<unknown>
}

type TProps = {
  query: TCardStateQuery
  skeletonClassName?: string
  /** The loaded content grows to fill the rest of a flex-column card. */
  fill?: boolean
  children: React.ReactNode
}

/**
 * Loading / error / refetching wrapper shared by the dashboard cards.
 * While a loaded card refetches (e.g. after a period change) it keeps its previous content dimmed.
 */
export const CardState: React.FC<TProps> = ({query, skeletonClassName, fill = false, children}) => {
  if (query.isPending) {
    return (
      <div
        aria-busy={"true"}
        data-testid={"card loading"}
        className={twMerge("h-24 w-full animate-pulse rounded-md bg-cr-grey-15", skeletonClassName)}
      />
    )
  }

  // A retry after a failed background refetch keeps the cached content visible as refetching.
  if (query.isError && !query.isFetching) {
    return <GenericErrorAlert retry={query.refetch} />
  }

  return (
    <div className={twMerge("relative", fill && "flex grow flex-col")} aria-busy={query.isFetching ? "true" : "false"}>
      <div className={twMerge("transition-opacity", fill && "flex grow flex-col", query.isFetching && "opacity-50")}>
        {children}
      </div>
      {query.isFetching && (
        <Loading size={"xs"} fullSpace={false} delayShow={false} containerClassName={"absolute top-0 right-0"} />
      )}
    </div>
  )
}
